import {useContext} from "react";

import {PageDisplayContext} from "./page-display-container.tsx";
import type {PartialPageSettings} from "./page-settings.ts";

/**
 * A hook that updates the page settings.
 *
 * @param pageSettings: A PartialPageSettings object used to update the settings.
 * @param resetSettings: Whether to reset the remaining settings to default
 * values when the update takes place. Defaults to true.
 *
 * Example usage:
 *   useUpdatePageSettings({title: "My Title"}, false);
 */
export function useUpdatePageSettings(
    pageSettings: PartialPageSettings,
    resetSettings = true,
): void {
    // NOTE (emilyjanzer): We call this function on every render, regardless
    // of if the pageSettings object has changed. This is because the
    // implementation of the update function in PageDisplayContainer will
    // handle making sure we don't update the page settings unnecessarily.
    // We could move this to an effect and only call the effect when the settings
    // have changed, but for now we're just trying to keep the API as similar
    // as possible to PageDisplay.
    const {update} = useContext(PageDisplayContext);
    update(pageSettings, resetSettings);
}
